import { useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useAllocatedApiCli,
  useAllocatedDedicatedCli,
  useAllocateApiCli,
  useAllocateDedicatedCli,
  useApiCliList,
  useCliList,
  useRemoveApiCli,
  useRemoveDedicatedCli,
} from '@/features/cli/api';
import type { CliRow } from '@/features/cli/types';

type Pool = 'api' | 'dedicated';
type Mode = 'manual' | 'bulk';

interface CliAllocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Target user receiving CLIs. */
  userId: number | string;
  username: string;
  /** Default pool tab to land on. */
  initialPool?: Pool;
}

/**
 * Single dialog that handles both API CLI and Dedicated CLI allocation for
 * one user. Two tabs select the pool; inside each tab two sections:
 *
 *   • Currently allocated — bulk-select & remove.
 *   • Allocate new — Manual (multi-select CSV) or Bulk (start + count).
 *
 * Replaces the legacy User Management page's pair of separate modals.
 * Same backend endpoints, cleaner UX.
 */
export function CliAllocationDialog({
  open,
  onOpenChange,
  userId,
  username,
  initialPool = 'api',
}: CliAllocationDialogProps) {
  const [pool, setPool] = useState<Pool>(initialPool);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Allocate CLI to {username}</DialogTitle>
          <DialogDescription>
            Grant or revoke CLI numbers for this user. Changes take effect
            immediately.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={pool} onValueChange={(v) => setPool(v as Pool)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="api">API CLI</TabsTrigger>
            <TabsTrigger value="dedicated">Dedicated CLI</TabsTrigger>
          </TabsList>

          <TabsContent value="api" className="mt-4">
            <CliAllocationPanel pool="api" userId={userId} />
          </TabsContent>

          <TabsContent value="dedicated" className="mt-4">
            <CliAllocationPanel pool="dedicated" userId={userId} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 *  Per-pool panel: allocated list + add controls
 * ============================================================ */

/**
 * One pool tab's contents (allocated table + allocate-new form). Exported
 * so the User Console drawer can embed it directly without nesting a
 * dialog inside a sheet.
 */
export function CliAllocationPanel({
  pool,
  userId,
}: {
  pool: Pool;
  userId: number | string;
}) {
  const allocatedApi = useAllocatedApiCli(pool === 'api' ? userId : null);
  const allocatedDed = useAllocatedDedicatedCli(
    pool === 'dedicated' ? userId : null,
  );
  const allocated = pool === 'api' ? allocatedApi : allocatedDed;

  // The "pool to draw from" is whatever the *caller* has access to. The
  // legacy app uses ACL-filtered API CLIs for the API pool and the
  // caller's own dedicated/shared CLIs for the dedicated pool.
  const apiPool = useApiCliList();
  const dedicatedPool = useCliList('cli');
  const sourcePool = pool === 'api' ? apiPool : dedicatedPool;

  return (
    <div className="grid gap-4">
      <AllocatedSection
        pool={pool}
        userId={userId}
        rows={allocated.data ?? []}
        isLoading={allocated.isLoading}
      />
      <AllocateSection
        pool={pool}
        userId={userId}
        sourceRows={sourcePool.data ?? []}
        isLoadingSource={sourcePool.isLoading}
      />
    </div>
  );
}

/* ============================================================
 *  "Currently allocated" — multi-select + remove
 * ============================================================ */

function AllocatedSection({
  pool,
  userId,
  rows,
  isLoading,
}: {
  pool: Pool;
  userId: number | string;
  rows: CliRow[];
  isLoading: boolean;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const removeApi = useRemoveApiCli();
  const removeDed = useRemoveDedicatedCli();
  const remove = pool === 'api' ? removeApi : removeDed;

  const allChecked = rows.length > 0 && selected.size === rows.length;
  const someChecked = selected.size > 0 && selected.size < rows.length;

  const toggleAll = (next: boolean) => {
    if (next) setSelected(new Set(rows.map((r) => r.cliId)));
    else setSelected(new Set());
  };

  const toggleOne = (id: number, next: boolean) => {
    setSelected((prev) => {
      const out = new Set(prev);
      if (next) out.add(id);
      else out.delete(id);
      return out;
    });
  };

  const handleRemove = () => {
    if (selected.size === 0) return;
    remove.mutate(
      {
        userId: String(userId),
        cliIds: Array.from(selected).join(','),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Removed ${selected.size} CLI(s)`);
          setSelected(new Set());
        },
        onError: () => toast.error('Could not remove the selected CLIs.'),
      },
    );
  };

  return (
    <section className="rounded-md border bg-card">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold">Currently allocated</h3>
          <p className="text-xs text-muted-foreground">
            {isLoading
              ? 'Loading…'
              : `${rows.length} CLI${rows.length === 1 ? '' : 's'} assigned`}
          </p>
        </div>
        <Button
          size="sm"
          variant="destructive"
          disabled={selected.size === 0 || remove.isPending}
          onClick={handleRemove}
        >
          {remove.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Remove {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </header>

      {rows.length === 0 && !isLoading ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          No CLIs allocated to this user yet.
        </div>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 border-b bg-muted text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-10 px-3 py-2 text-center">
                  <Checkbox
                    checked={
                      allChecked
                        ? true
                        : someChecked
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={(v) => toggleAll(Boolean(v))}
                    aria-label="Select all"
                  />
                </th>
                <th className="px-3 py-2">CLI</th>
                <th className="px-3 py-2">Pilot</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.cliId} className="border-b last:border-b-0">
                  <td className="px-3 py-2 text-center">
                    <Checkbox
                      checked={selected.has(r.cliId)}
                      onCheckedChange={(v) => toggleOne(r.cliId, Boolean(v))}
                      aria-label={`Select ${r.cli}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono">{r.cli}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.pilotNumber ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.cliStatus === 1 ? 'Active' : 'Archived'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ============================================================
 *  "Allocate new" — Manual / Bulk modes
 * ============================================================ */

function AllocateSection({
  pool,
  userId,
  sourceRows,
  isLoadingSource,
}: {
  pool: Pool;
  userId: number | string;
  sourceRows: CliRow[];
  isLoadingSource: boolean;
}) {
  const [mode, setMode] = useState<Mode>('manual');
  const [manualPicks, setManualPicks] = useState<string[]>([]);
  const [bulkStartCli, setBulkStartCli] = useState('');
  const [bulkRange, setBulkRange] = useState('');

  const allocateApi = useAllocateApiCli();
  const allocateDed = useAllocateDedicatedCli();
  const allocate = pool === 'api' ? allocateApi : allocateDed;

  const options = useMemo(
    () =>
      sourceRows.map((c) => ({
        value: String(c.cliId),
        label: c.pilotNumber ? `${c.cli} (${c.pilotNumber})` : c.cli,
      })),
    [sourceRows],
  );

  const reset = () => {
    setManualPicks([]);
    setBulkStartCli('');
    setBulkRange('');
  };

  const handleSubmit = () => {
    if (mode === 'manual') {
      if (manualPicks.length === 0) {
        toast.error('Pick at least one CLI.');
        return;
      }
      allocate.mutate(
        {
          userId: String(userId),
          allocationType: 'manual',
          cli: manualPicks.join(','),
        },
        {
          onSuccess: (res) => {
            if (res?.message === 'Parameters Incorrect') {
              toast.error(res.message);
              return;
            }
            toast.success(res?.message ?? 'CLIs allocated');
            reset();
          },
          onError: () => toast.error('Could not allocate the CLIs.'),
        },
      );
      return;
    }

    if (!bulkStartCli) {
      toast.error('Pick a starting CLI.');
      return;
    }
    const n = Number(bulkRange);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error('Enter a positive range.');
      return;
    }
    allocate.mutate(
      {
        userId: String(userId),
        allocationType: 'bulk',
        cli: bulkStartCli,
        cliRange: String(n),
      },
      {
        onSuccess: (res) => {
          if (res?.message === 'Parameters Incorrect') {
            toast.error(res.message);
            return;
          }
          toast.success(res?.message ?? 'CLIs allocated');
          reset();
        },
        onError: () => toast.error('Could not allocate the CLIs.'),
      },
    );
  };

  return (
    <section className="rounded-md border bg-card">
      <header className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Allocate new</h3>
        <p className="text-xs text-muted-foreground">
          Pick CLIs from your pool. Bulk mode grants a contiguous range from
          the chosen starting CLI.
        </p>
      </header>

      <div className="space-y-4 px-4 py-3">
        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as Mode)}
          className="grid grid-cols-2 gap-3"
        >
          <label className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 hover:border-ring">
            <RadioGroupItem value="manual" />
            <div>
              <div className="text-sm font-medium">Manual</div>
              <div className="text-xs text-muted-foreground">
                Pick specific CLIs from a list.
              </div>
            </div>
          </label>
          <label className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 hover:border-ring">
            <RadioGroupItem value="bulk" />
            <div>
              <div className="text-sm font-medium">Bulk</div>
              <div className="text-xs text-muted-foreground">
                Grant N consecutive CLIs from a starting point.
              </div>
            </div>
          </label>
        </RadioGroup>

        {mode === 'manual' ? (
          <div className="grid gap-1.5">
            <Label>CLIs to allocate</Label>
            <MultiSelect
              options={options}
              value={manualPicks}
              onChange={setManualPicks}
              placeholder={
                isLoadingSource
                  ? 'Loading your CLI pool…'
                  : 'Pick one or more CLIs'
              }
              searchPlaceholder="Filter CLIs…"
              disabled={isLoadingSource}
            />
            <p className="text-xs text-muted-foreground">
              {manualPicks.length} selected.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="bulkStart">Starting CLI</Label>
              <Select
                value={bulkStartCli}
                onValueChange={setBulkStartCli}
                disabled={isLoadingSource}
              >
                <SelectTrigger id="bulkStart">
                  <SelectValue
                    placeholder={
                      isLoadingSource ? 'Loading…' : 'Pick a starting CLI'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="bulkRange">Range (count)</Label>
              <Input
                id="bulkRange"
                inputMode="numeric"
                min="1"
                value={bulkRange}
                onChange={(e) =>
                  setBulkRange(e.target.value.replace(/\D/g, ''))
                }
                placeholder="e.g. 10"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={allocate.isPending}>
            {allocate.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Allocate
          </Button>
        </div>
      </div>
    </section>
  );
}
